import clsx from 'clsx'
import {
    forwardRef,
    MutableRefObject,
    ReactNode,
    Ref,
    useEffect,
    useId,
    useRef,
    useState,
} from 'react'

export type CheckboxSize = 'sm' | 'md'

export interface CheckboxProps {
    checked?: boolean
    defaultChecked?: boolean
    indeterminate?: boolean
    onChange?: (checked: boolean) => void
    size?: CheckboxSize
    label?: ReactNode
    supportingText?: ReactNode
    disabled?: boolean
    id?: string
    name?: string
    value?: string
    className?: string
}

interface SizeClasses {
    box: string
    glyph: string
    strokeWidth: number
    row: string
    text: string
    textGroup: string
}

const SIZE_CLASSES: Record<CheckboxSize, SizeClasses> = {
    sm: {
        box: 'size-4 rounded-radius-xs',
        glyph: 'size-3',
        strokeWidth: 3.33,
        row: 'gap-spacing-md',
        text: 'text-sm',
        textGroup: 'gap-spacing-none',
    },
    md: {
        box: 'size-5 rounded-radius-sm',
        glyph: 'size-3.5',
        strokeWidth: 3.43,
        row: 'gap-spacing-lg',
        text: 'text-md',
        textGroup: 'gap-spacing-xxs',
    },
}

const assignRef = (ref: Ref<HTMLInputElement> | undefined, node: HTMLInputElement | null) => {
    if (typeof ref === 'function') {
        ref(node)
    } else if (ref) {
        ;(ref as MutableRefObject<HTMLInputElement | null>).current = node
    }
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
    (
        {
            checked,
            defaultChecked,
            indeterminate = false,
            onChange,
            size = 'sm',
            label,
            supportingText,
            disabled = false,
            id: providedId,
            name,
            value,
            className,
        },
        ref,
    ) => {
        const generatedId = useId()
        const id = providedId || generatedId
        const sizeClasses = SIZE_CLASSES[size]

        const [internalChecked, setInternalChecked] = useState(defaultChecked ?? false)
        const isControlled = checked !== undefined
        const checkedValue = isControlled ? checked : internalChecked
        const isFilled = checkedValue || indeterminate

        // `indeterminate` is a DOM property, not an attribute, so it is set on the node.
        const innerRef = useRef<HTMLInputElement | null>(null)
        useEffect(() => {
            if (innerRef.current) {
                innerRef.current.indeterminate = indeterminate
            }
        }, [indeterminate])

        const handleChange = () => {
            if (disabled) {
                return
            }
            const nextChecked = !checkedValue
            if (!isControlled) {
                setInternalChecked(nextChecked)
            }
            onChange?.(nextChecked)
        }

        const getBoxStateClasses = () => {
            if (isFilled) {
                return 'border-border-brand bg-background-bg-brand-solid text-foreground-fg-white'
            }
            if (disabled) {
                return 'border-border-primary bg-background-bg-tertiary'
            }
            return 'border-border-primary'
        }

        const boxClasses = clsx(
            'inline-flex shrink-0 items-center justify-center border border-solid',
            'peer-focus-visible:ring-2 peer-focus-visible:ring-focus-ring peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-base-white',
            disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
            sizeClasses.box,
            getBoxStateClasses(),
        )

        // Inline SVG rather than an icon font: the global Material Symbols reset
        // (`max-width: 1em; overflow: hidden`) clips small glyphs inside the box.
        const renderGlyph = () => {
            if (indeterminate) {
                return (
                    <svg
                        className={sizeClasses.glyph}
                        viewBox="0 0 24 24"
                        fill="none"
                        aria-hidden="true">
                        <path
                            d="M5 12h14"
                            stroke="currentColor"
                            strokeWidth={sizeClasses.strokeWidth}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                )
            }
            if (checkedValue) {
                return (
                    <svg
                        className={sizeClasses.glyph}
                        viewBox="0 0 24 24"
                        fill="none"
                        aria-hidden="true">
                        <path
                            d="M20 6 9 17l-5-5"
                            stroke="currentColor"
                            strokeWidth={sizeClasses.strokeWidth}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                )
            }
            return null
        }

        const control = (
            <>
                <input
                    ref={(node) => {
                        innerRef.current = node
                        assignRef(ref, node)
                    }}
                    type="checkbox"
                    id={id}
                    name={name}
                    value={value}
                    checked={checkedValue}
                    disabled={disabled}
                    aria-checked={indeterminate ? 'mixed' : checkedValue}
                    onChange={handleChange}
                    className="peer sr-only"
                />
                <label htmlFor={id} className={boxClasses}>
                    {renderGlyph()}
                </label>
            </>
        )

        if (!label && !supportingText) {
            return <div className={clsx('inline-flex', className)}>{control}</div>
        }

        return (
            <div className={clsx('inline-flex items-start font-body', sizeClasses.row, className)}>
                <div className="flex shrink-0 items-center pt-spacing-xxs">{control}</div>
                <div className={clsx('flex flex-col', sizeClasses.textGroup)}>
                    {label && (
                        <label
                            htmlFor={id}
                            className={clsx(
                                'font-medium text-text-secondary',
                                sizeClasses.text,
                                disabled ? 'cursor-not-allowed' : 'cursor-pointer',
                            )}>
                            {label}
                        </label>
                    )}
                    {supportingText && (
                        <div className={clsx('font-regular text-text-tertiary', sizeClasses.text)}>
                            {supportingText}
                        </div>
                    )}
                </div>
            </div>
        )
    },
)

Checkbox.displayName = 'Checkbox'
